import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import { MIN_GTG_CPC_USD, MIN_GTG_CPM_USD } from "@healthdesk/shared-utils";
import {
  US_STATES,
  US_BASE,
  KE_BASE,
  US_TIER_MULT,
  US_TIER_LABEL,
  KE_TIER_MULT,
  KE_TIER_LABEL,
  KE_COUNTIES,
  usStateTier,
  keCountyTier,
  AD_RATE_AD_TYPE_TEXT,
  AD_RATE_AD_TYPE_VIDEO,
  applyAdRateMults,
  formatAdRateMoney,
  REFERENCE_CAVEATS,
} from "./healthcareAdRateReferenceData";

function rangePct(v, cap) {
  return Math.min(Math.round((v / cap) * 100), 98);
}

function RangeBar({ min, typical, premium, color }) {
  const theme = useTheme();
  const cap = premium * 1.2;
  const pMin = rangePct(min, cap);
  const pTyp0 = rangePct(typical[0], cap);
  const pTyp1 = rangePct(typical[1], cap);
  const pPrem = rangePct(premium, cap);
  const barWidth = Math.max(pTyp1 - pTyp0, 2);

  return (
    <Box
      sx={{
        position: "relative",
        height: 20,
        borderRadius: 999,
        bgcolor: theme.palette.action.hover,
        overflow: "hidden",
        my: 1,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: `${pMin}%`,
          height: "100%",
          width: 4,
          borderRadius: 1,
          bgcolor: color,
          opacity: 0.4,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 4,
          left: `${pTyp0}%`,
          width: `${barWidth}%`,
          height: 12,
          borderRadius: 999,
          bgcolor: color,
          opacity: 0.75,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: `${pPrem}%`,
          height: "100%",
          width: 6,
          borderRadius: 1,
          bgcolor: color,
        }}
      />
    </Box>
  );
}

function AudienceCard({ aud, adType }) {
  const theme = useTheme();
  const paletteKey = aud.paletteColor || "primary";
  const pal = theme.palette[paletteKey] ?? theme.palette.primary;
  const main = pal.main;
  const lightBg = alpha(main, 0.08);
  const borderCol = alpha(main, 0.3);
  const useCpm = adType === AD_RATE_AD_TYPE_VIDEO && aud.cpm != null;
  const m = useCpm ? aud.cpm : aud.cpc;
  const notesList = aud.notes?.[adType] ?? [];

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: borderCol,
        bgcolor: lightBg,
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {aud.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {aud.subtitle}
            </Typography>
          </Box>
          <Chip
            label={useCpm ? "CPM" : "CPC"}
            size="small"
            sx={{ bgcolor: main, color: pal.contrastText, fontWeight: 700 }}
          />
        </Stack>

        <RangeBar min={m.min} typical={m.typical} premium={m.premium} color={main} />
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.disabled">
            Min
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Typical
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Premium
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              p: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.disabled" display="block">
              Minimum
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {formatAdRateMoney(m.min)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              p: 1,
              textAlign: "center",
              bgcolor: main,
              color: pal.contrastText,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.9 }} display="block">
              Typical range
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {formatAdRateMoney(m.typical[0])} – {formatAdRateMoney(m.typical[1])}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              p: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.disabled" display="block">
              Premium
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {formatAdRateMoney(m.premium)}
              {m.premiumSuffix}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ borderTop: 1, borderColor: borderCol, pt: 1.5 }}>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {notesList.map((n, i) => (
              <Typography
                key={i}
                component="li"
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}
              >
                <Box component="span" sx={{ color: main, mt: 0.25, flexShrink: 0 }}>
                  ▸
                </Box>
                <span>{n}</span>
              </Typography>
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

/** DOM id for scroll targets (e.g. Local Carousel Ads “open rate reference” links). */
export const CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID =
  "caremap-healthcare-ad-rate-reference";

/**
 * Healthcare CPC reference: ad format notes (plain vs YouTube modal), country, optional US state / Kenya county geo tiers (MUI). Not financial advice.
 *
 * @param {{ expanded?: boolean, onExpandedChange?: (expanded: boolean) => void }} [props]
 */
const CareMapAdRateReference = ({ expanded: expandedProp, onExpandedChange }) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [country, setCountry] = useState("US");
  const [usState, setUsState] = useState("");
  const [keCounty, setKeCounty] = useState("");
  const [adType, setAdType] = useState(AD_RATE_AD_TYPE_TEXT);

  const { audiences, tierLabel, geoLabel } = useMemo(() => {
    if (country === "US") {
      const mult = usState ? US_TIER_MULT[usStateTier(usState)] : 1.0;
      const tier = usState ? usStateTier(usState) : null;
      return {
        audiences: US_BASE.map((a) => applyAdRateMults(a, mult)),
        tierLabel: tier ? US_TIER_LABEL[tier] : null,
        geoLabel: usState ? US_STATES.find((s) => s[0] === usState)?.[1] : "National average",
      };
    }
    const mult = keCounty ? KE_TIER_MULT[keCountyTier(keCounty)] : 1.0;
    const tier = keCounty ? keCountyTier(keCounty) : null;
    return {
      audiences: KE_BASE.map((a) => applyAdRateMults(a, mult)),
      tierLabel: tier ? KE_TIER_LABEL[tier] : null,
      geoLabel: keCounty || "National average",
    };
  }, [country, usState, keCounty]);

  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    setUsState("");
    setKeCounty("");
  };

  const expanded = expandedProp !== undefined ? expandedProp : internalExpanded;
  const handleAccordionChange = (_event, isExpanded) => {
    if (expandedProp !== undefined) {
      onExpandedChange?.(isExpanded);
    } else {
      setInternalExpanded(isExpanded);
    }
  };

  return (
    <>
      <Box
        id={CAREMAP_HEALTHCARE_AD_RATE_REFERENCE_ANCHOR_ID}
        sx={{ scrollMarginTop: { xs: 72, sm: 80 } }}
      >
        <Accordion
          variant="outlined"
          sx={{ mt: 2 }}
          expanded={expanded}
          onChange={handleAccordionChange}
        >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Healthcare ad rate reference</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            /* MUI default is pt=1, pb=2 (asymmetric). Equal top/bottom; py:3 adds a bit more bottom inset than default pb=2. */
            py: 3,
            px: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" paragraph>
            Figures below are <strong>industry reference ranges</strong> for digital healthcare marketing:{" "}
            <strong>CPC</strong> for plain carousel links and <strong>CPM</strong> (cost per thousand impressions) for
            YouTube video slides on CareMap. They are not guarantees. US targeting enforces a minimum{" "}
            <strong>max CPC of ${MIN_GTG_CPC_USD} USD</strong> when you use standard links and a minimum{" "}
            <strong>max CPM of ${MIN_GTG_CPM_USD} USD</strong> when you use “Link to video.” Subcategory or specialty
            targeting may be offered separately.
          </Typography>

          <Box
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
              p: 2,
              mb: 2,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 1 }}>
                  Creative format (benchmark notes)
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={adType}
                  onChange={(_e, next) => {
                    if (next != null) setAdType(next);
                  }}
                  aria-label="Creative format for benchmark notes"
                >
                  <ToggleButton value={AD_RATE_AD_TYPE_TEXT}>Plain text + logo</ToggleButton>
                  <ToggleButton value={AD_RATE_AD_TYPE_VIDEO}>YouTube video (modal)</ToggleButton>
                </ToggleButtonGroup>
                {adType === AD_RATE_AD_TYPE_VIDEO ? (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                      <strong>CareMap billing:</strong> When you select <strong>Link to video</strong> on a carousel
                      slide, prepaid is charged on <strong>CPM</strong>—each time that slide is <strong>shown</strong> in
                      the carousel (proportional to your max CPM), not when the user taps “Watch video.” The ranges
                      above switch to <strong>CPM</strong> planning tiers; bullets describe video context.
                    </Typography>
                  </Alert>
                ) : null}
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel id="caremap-ad-rate-country">Country</InputLabel>
                <Select
                  labelId="caremap-ad-rate-country"
                  label="Country"
                  value={country}
                  onChange={handleCountryChange}
                >
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="KE">Kenya</MenuItem>
                </Select>
              </FormControl>

              {country === "US" && (
                <FormControl fullWidth size="small">
                  <InputLabel id="caremap-ad-rate-us-state">State (optional)</InputLabel>
                  <Select
                    labelId="caremap-ad-rate-us-state"
                    label="State (optional)"
                    value={usState}
                    onChange={(e) => setUsState(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>National average</em>
                    </MenuItem>
                    {US_STATES.map(([code, name]) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {country === "KE" && (
                <FormControl fullWidth size="small">
                  <InputLabel id="caremap-ad-rate-ke-county">County (optional)</InputLabel>
                  <Select
                    labelId="caremap-ad-rate-ke-county"
                    label="County (optional)"
                    value={keCounty}
                    onChange={(e) => setKeCounty(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>National average</em>
                    </MenuItem>
                    {KE_COUNTIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ pt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing rates for:
                </Typography>
                <Chip
                  size="small"
                  label={`${country === "US" ? "US" : "KE"} · ${geoLabel}`}
                  sx={{ fontWeight: 600 }}
                />
                {tierLabel ? (
                  <Typography variant="caption" color="text.secondary" fontStyle="italic">
                    {tierLabel}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            US & Kenya benchmarks · 2024–2025 planning reference. Dollar ranges scale only with geo tiers; YouTube
            (modal) uses CPC on CareMap, not watch-based billing. Kenya figures use USD-style display for comparison;
            local buying may be in KES.
          </Typography>

          <Stack spacing={2} sx={{ mb: 2 }}>
            {audiences.map((aud) => (
              <AudienceCard key={aud.id} aud={aud} adType={adType} />
            ))}
          </Stack>

          <Box
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.default",
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, lineHeight: 1.6 }}>
              Figures are <strong>industry CPC planning ranges</strong>. CareMap debits prepaid balance on{" "}
              <strong>eligible clicks</strong> (including clicks that open an embedded YouTube video), per your{" "}
              <strong>max CPC per geographic targeting group</strong>—open <strong>CareMap billing details</strong>{" "}
              below for product-specific terms.
            </Typography>
            <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" display="block" gutterBottom>
              Key caveats
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
              {REFERENCE_CAVEATS.map((c, i) => (
                <Typography key={i} component="li" variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {c}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Box>
            <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)}>
              CareMap billing details
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reference details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            Benchmarks depend on channel (search, social, endemic medical sites), seasonality, creative, and audience
            definitions. CareMap bills eligible clicks against your prepaid balance using your{" "}
            <strong>max CPC per geographic targeting group</strong> (see enforced US floor above)—including when the
            click opens a YouTube video in a modal. Your contract governs what counts as an eligible click.
          </Typography>
          <Typography variant="body2" paragraph>
            State and county selectors apply <strong>competition-tier multipliers</strong> to national baselines—they are
            planning guides, not separate published minimums for every subdivision. The <strong>YouTube video</strong>{" "}
            toggle only changes contextual bullet notes; it does not add watch-based charges in CareMap.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contact your HealthDesk representative if you need a formal media kit or custom reporting.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CareMapAdRateReference;
